import {ReactElement, useEffect, useState} from "react";

import {Field, Hint, Input, Label, Message} from "@zendeskgarden/react-forms";
import {parseNumber} from "../../../../../../../utils/string-helpers";
import * as css from "./styles/css.module.scss";


interface MinMaxFieldsProps {
    minValue: string | null;
    maxValue: string | null;
    hideHintText?: boolean;
    onChange?: (vals: {
        minValue: string | null,
        maxValue: string | null,
    }) => void;
    onErrorUpdate?: (message: string | null) => void;
}

/**
 * Prisma needs Min and Max values as Decimals, so this helper takes an input, parses it to be
 * a valid number string, if possible, and otherwise returns null, since Decimal or null are both valid
 * types for these fields. THIS IS JUST FOR TYPE COERCION, NOT FOR ERROR HANDLING. It will convert invalid number strings like
 * "abc" to null entries. Decide how you want to handle error checking before getting to this point.
 */
export function transformMinMaxValueToDecimalString(val: number | string | null | undefined): string | null {
    const valToSave: number | null | string = parseNumber(val);
    return valToSave === null ? null : String(valToSave);
}

export default function MinMaxFields(props: MinMaxFieldsProps): ReactElement {
    const [minValue, setMinValue] = useState<string | null>(props.minValue);
    const [maxValue, setMaxValue] = useState<string | null>(props.maxValue);
    const [minMaxError, setMinMaxError] = useState<string | null>(null);

    useEffect(() => {
        setMinValue(props.minValue);
    }, [props.minValue]);

    useEffect(() => {
        setMaxValue(props.maxValue);
    }, [props.maxValue]);

    useEffect(() => {
        let error: string | null = null;
        const minValueNum = parseNumber(minValue);
        const maxValueNum = parseNumber(maxValue);

        if (typeof minValueNum === "number" && typeof maxValueNum === "number" && minValueNum > maxValueNum) {
            error = "Min cannot be higher than max.";
        }

        if ((minValueNum != null && isNaN(minValueNum)) || (maxValueNum != null && isNaN(maxValueNum))) {
            error = "Values must be numbers only.";
        }

        setMinMaxError(error);
        if (minValue !== props.minValue || maxValue !== props.maxValue) {
            props.onChange?.({minValue, maxValue});
        }
    }, [minValue, maxValue]);

    useEffect(() => {
        props.onErrorUpdate?.(minMaxError);
    }, [minMaxError]);

    return (
        <div className={css.minMaxFields}>
            {!props.hideHintText &&
                <Hint>Set limits for how much this driver can contribute each month.</Hint>
            }
            <div className={css.sideBySideInputs}>
                <Field>
                    <Label>$ Min</Label>
                    <Input
                        value={minValue ?? ""}
                        placeholder="No Min"
                        isCompact
                        onChange={evt => setMinValue(evt.target.value.trim() === "" ? "" : evt.target.value)}
                        validation={minMaxError != null ? "error" : undefined}
                        />
                </Field>
                <Field>
                    <Label>$ Max</Label>
                    <Input
                        value={maxValue ?? ""}
                        placeholder="No Max"
                        isCompact
                        onChange={evt => setMaxValue(evt.target.value.trim() === "" ? "" : evt.target.value)}
                        validation={minMaxError != null ? "error" : undefined}
                        />
                </Field>
            </div>
            {minMaxError &&
                <Message validation="error">{minMaxError}</Message>
            }
        </div>
    );
}